/**
 * Low-level cryptographic primitives for the `e2e-crypto` kit (canon
 * Boveda/P-3, ZERO_KNOWLEDGE_VAULT §2).
 *
 * Everything relies on WebCrypto (`crypto.subtle`), available in browsers, Bun
 * and Node ≥ 18 — with no native dependencies. The only exception is the KDF
 * (Argon2id), which WebCrypto does not provide and lives in `kdf.ts` (@noble/hashes).
 *
 * AEAD: AES-256-GCM. WebCrypto appends the authentication tag (128 bits) to the end
 * of the ciphertext, so an envelope is `iv[12] || ct+tag`.
 * Each envelope carries **AAD** (unencrypted authenticated data) that binds the
 * ciphertext to its context: an envelope cannot be reused outside its
 * context without the open failing.
 /** Waits for the app to finish booting (skeleton overlay disappears). */

import { utf8Encode } from "./encoding";

const AES_KEY_BYTES = 32; // AES-256
const GCM_IV_BYTES = 12;
const GCM_TAG_BITS = 128;

/** Copies to an owned ArrayBuffer: avoids typing friction with BufferSource. */
function ab(bytes: Uint8Array): ArrayBuffer {
  const copy = new ArrayBuffer(bytes.length);
  new Uint8Array(copy).set(bytes);
  return copy;
}

export function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

export async function importAesKey(
  raw: Uint8Array,
  usages: KeyUsage[] = ["encrypt", "decrypt"],
): Promise<CryptoKey> {
  if (raw.length !== AES_KEY_BYTES) {
    throw new Error("AES key must be 32 bytes");
  }
  return crypto.subtle.importKey("raw", ab(raw), { name: "AES-GCM" }, false, usages);
}

/**
 * Seals `plaintext` with AES-256-GCM under `aad`. Returns raw bytes from the
 * envelope (`iv || ct+tag`).
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export async function aeadSeal(
  key: CryptoKey,
  plaintext: Uint8Array,
  aad: string,
): Promise<Uint8Array> {
  const iv = randomBytes(GCM_IV_BYTES);
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: ab(iv), additionalData: ab(utf8Encode(aad)), tagLength: GCM_TAG_BITS },
    key,
    ab(plaintext),
  );
  const out = new Uint8Array(GCM_IV_BYTES + ct.byteLength);
  out.set(iv, 0);
  out.set(new Uint8Array(ct), GCM_IV_BYTES);
  return out;
}

export async function aeadOpen(
  key: CryptoKey,
  envelope: Uint8Array,
  aad: string,
): Promise<Uint8Array> {
  if (envelope.length <= GCM_IV_BYTES) {
    throw new Error("envelope too short");
  }
  const iv = envelope.subarray(0, GCM_IV_BYTES);
  const ct = envelope.subarray(GCM_IV_BYTES);
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: ab(iv), additionalData: ab(utf8Encode(aad)), tagLength: GCM_TAG_BITS },
    key,
    ab(ct),
  );
  return new Uint8Array(plaintext);
}

/**
 * HKDF-Expand (RFC 5869) over `ikm` with a domain-separation tag `info`. It is used
 * to derive separate subkeys from the same master key, guaranteeing
 * domain separation: knowing one does not reveal the other.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export async function hkdfExpand(
  ikm: Uint8Array,
  info: string,
  length = AES_KEY_BYTES,
  salt: Uint8Array = new Uint8Array(0),
): Promise<Uint8Array> {
  const base = await crypto.subtle.importKey("raw", ab(ikm), "HKDF", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits(
    { name: "HKDF", hash: "SHA-256", salt: ab(salt), info: ab(utf8Encode(info)) },
    base,
    length * 8,
  );
  return new Uint8Array(bits);
}

export async function sha256(bytes: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.digest("SHA-256", ab(bytes)));
}
